import React from 'react';
import './AvailabilityCalendar.css';

interface AvailabilityCalendarProps {
  selectedDate: Date;
  displayedMonth: Date;
  leaveSlots: LeaveSlot[];
  appointmentCounts: Record<string, number>;
  onDateSelect: (date: Date) => void;
  onMonthChange: (month: Date) => void;
  hasFullDayLeave: (date: Date) => boolean;
  hasPartialLeave: (date: Date) => boolean;
  isDateEnabled: (date: Date) => boolean;
  isLoadingAppointmentCounts: boolean;
}

interface LeaveSlot {
  leave_date: string;
  start_time: string;
  end_time: string;
  status: string;
}

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  selectedDate,
  displayedMonth,
  leaveSlots,
  appointmentCounts,
  onDateSelect,
  onMonthChange,
  hasFullDayLeave,
  hasPartialLeave,
  isDateEnabled,
  isLoadingAppointmentCounts,
}) => {
  const calendar = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get first day of month and first day of week
  const firstDayOfMonth = new Date(calendar.getFullYear(), calendar.getMonth(), 1);
  const firstDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday, 6 = Saturday

  // Get last day of month
  const lastDayOfMonth = new Date(calendar.getFullYear(), calendar.getMonth() + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();

  // Get previous month's trailing days
  const prevMonth = new Date(calendar.getFullYear(), calendar.getMonth() - 1, 0);
  const daysInPrevMonth = prevMonth.getDate();

  // Get next month's leading days
  const totalDays = firstDayOfWeek + daysInMonth;
  const weeksToShow = Math.ceil(totalDays / 7);
  const daysToShow = weeksToShow * 7;

  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const isToday = (date: Date): boolean => {
    return isSameDay(date, today);
  };

  const isSelected = (date: Date): boolean => {
    return isSameDay(date, selectedDate);
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === calendar.getMonth() && date.getFullYear() === calendar.getFullYear();
  };

  const getAppointmentCount = (date: Date): number => {
    const dateString = formatDateString(date);
    return appointmentCounts[dateString] || 0;
  };

  const handlePreviousMonth = () => {
    const newMonth = new Date(calendar.getFullYear(), calendar.getMonth() - 1, 1);
    const minMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    if (newMonth >= minMonth) {
      onMonthChange(newMonth);
    }
  };

  const handleNextMonth = () => {
    const newMonth = new Date(calendar.getFullYear(), calendar.getMonth() + 1, 1);
    onMonthChange(newMonth);
  };

  const canGoToPreviousMonth = () => {
    const minMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return calendar > minMonth;
  };

  const monthYearString = calendar.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Generate all days to display
  const days: Date[] = [];
  
  // Previous month's trailing days
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const date = new Date(calendar.getFullYear(), calendar.getMonth() - 1, daysInPrevMonth - i);
    days.push(date);
  }
  
  // Current month's days
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(calendar.getFullYear(), calendar.getMonth(), i);
    days.push(date);
  }
  
  // Next month's leading days
  const remainingDays = daysToShow - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(calendar.getFullYear(), calendar.getMonth() + 1, i);
    days.push(date);
  }

  return (
    <div className="availability-calendar">
      {/* Month Navigation */}
      <div className="calendar-header">
        <button
          type="button"
          className="calendar-nav-button"
          onClick={handlePreviousMonth}
          disabled={!canGoToPreviousMonth()}
        >
          ←
        </button>
        <div className="calendar-month-year">{monthYearString}</div>
        <button
          type="button"
          className="calendar-nav-button"
          onClick={handleNextMonth}
        >
          →
        </button>
      </div>

      {isLoadingAppointmentCounts && (
        <div className="calendar-loading">Loading...</div>
      )}

      {/* Day Headers */}
      <div className="calendar-weekdays">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="calendar-weekday">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {days.map((date, index) => {
          const dateStr = formatDateString(date);
          const currentMonth = isCurrentMonth(date);
          const enabled = isDateEnabled(date);
          const selected = isSelected(date);
          const todayDate = isToday(date);
          const fullDayLeave = hasFullDayLeave(date);
          const partialLeave = hasPartialLeave(date);
          const appointmentCount = getAppointmentCount(date);

          return (
            <button
              key={`${dateStr}-${index}`}
              type="button"
              className={`calendar-day ${!currentMonth ? 'calendar-day-other-month' : ''} ${!enabled ? 'calendar-day-disabled' : ''} ${selected ? 'calendar-day-selected' : ''} ${todayDate ? 'calendar-day-today' : ''}`}
              onClick={() => enabled && onDateSelect(date)}
              disabled={!enabled}
            >
              <div className="calendar-day-number">{date.getDate()}</div>
              <div className="calendar-day-indicators">
                {appointmentCount > 0 && (
                  <span className="calendar-appointment-count">
                    ({appointmentCount > 9 ? '9+' : appointmentCount})
                  </span>
                )}
                {fullDayLeave && <span className="calendar-indicator calendar-indicator-full-day"></span>}
                {!fullDayLeave && partialLeave && (
                  <span className="calendar-indicator calendar-indicator-partial"></span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        <div className="calendar-legend-item">
          <span className="calendar-legend-dot calendar-legend-dot-red"></span>
          <span>Full Day Leave</span>
        </div>
        <div className="calendar-legend-item">
          <span className="calendar-legend-dot calendar-legend-dot-orange"></span>
          <span>Partial Leave</span>
        </div>
        <div className="calendar-legend-item">
          <span className="calendar-legend-dot calendar-legend-dot-blue"></span>
          <span>Today</span>
        </div>
        <div className="calendar-legend-item">
          <span className="calendar-legend-dot calendar-legend-dot-gray"></span>
          <span>(*) No of Appointments</span>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;

